import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Alert } from '../components/common/Alert';
import { Badge } from '../components/common/Badge';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useNotification } from '../contexts/NotificationContext';

interface NipAccess {
  id: number;
  nip: string;
  created_at: string;
  assignedBy: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  invoice_access: boolean;
  invoice_only_access: boolean;
}

interface NipForm {
  nips: string;
}

export const UserNipManagement = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<NipForm>();

  // Get user details
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    },
    enabled: !!userId
  });

  // Get user's NIP access
  const { data: nipAccess, isLoading: nipLoading } = useQuery({
    queryKey: ['userNipAccess', userId],
    queryFn: async () => {
      const response = await api.get(`/user-nip-access/users/${userId}`);
      return response.data;
    },
    enabled: !!userId
  });

  // Update user invoice access
  const updateAccess = useMutation({
    mutationFn: async ({ invoiceAccess, invoiceOnlyAccess }: { invoiceAccess: boolean; invoiceOnlyAccess: boolean }) => {
      const response = await api.put(`/user-nip-access/users/${userId}/access`, {
        invoice_access: invoiceAccess,
        invoice_only_access: invoiceOnlyAccess
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      showNotification({
        type: 'success',
        title: 'Sukces',
        message: 'Uprawnienia zostały zaktualizowane'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: error.response?.data?.error || 'Nie udało się zaktualizować uprawnień'
      });
    }
  });

  // Assign NIPs
  const assignNips = useMutation({
    mutationFn: async (data: NipForm) => {
      const nipsArray = data.nips
        .split('\n')
        .map(nip => nip.trim())
        .filter(nip => nip.length > 0);
      
      const response = await api.post(`/user-nip-access/users/${userId}/nips`, {
        nips: nipsArray
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNipAccess', userId] });
      reset();
      showNotification({
        type: 'success',
        title: 'Sukces',
        message: 'NIPy zostały przypisane'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: error.response?.data?.error || 'Nie udało się przypisać NIPów'
      });
    }
  });

  // Remove NIP access
  const removeNip = useMutation({
    mutationFn: async (nipAccessId: number) => {
      const response = await api.delete(`/user-nip-access/users/${userId}/nips/${nipAccessId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNipAccess', userId] });
      showNotification({
        type: 'success',
        title: 'Sukces',
        message: 'NIP został usunięty'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Błąd',
        message: error.response?.data?.error || 'Nie udało się usunąć NIP'
      });
    }
  });

  const onSubmit = (data: NipForm) => {
    assignNips.mutate(data);
  };

  if (userLoading || nipLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Alert variant="error">Użytkownik nie został znaleziony</Alert>;
  }

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Zarządzanie dostępem do faktur
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Zarządzaj dostępem do faktur dla użytkownika: {user.first_name} {user.last_name} ({user.email})
        </p>
      </div>

      {/* Invoice Access Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Ustawienia dostępu</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium">Dostęp do faktur</h3>
                <p className="text-sm text-gray-500">
                  Czy użytkownik ma dostęp do systemu faktur
                </p>
              </div>
              <Button
                variant={user.invoice_access ? "destructive" : "default"}
                onClick={() => updateAccess.mutate({ 
                  invoiceAccess: !user.invoice_access, 
                  invoiceOnlyAccess: user.invoice_only_access 
                })}
                disabled={updateAccess.isPending}
              >
                {user.invoice_access ? 'Wyłącz dostęp' : 'Włącz dostęp'}
              </Button>
            </div>

            {user.invoice_access && (
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Tylko faktury</h3>
                  <p className="text-sm text-gray-500">
                    Ogranicza dostęp tylko do faktur (bez zarządzania urlopami)
                  </p>
                </div>
                <Button
                  variant={user.invoice_only_access ? "destructive" : "outline"}
                  onClick={() => updateAccess.mutate({ 
                    invoiceAccess: user.invoice_access, 
                    invoiceOnlyAccess: !user.invoice_only_access 
                  })}
                  disabled={updateAccess.isPending}
                >
                  {user.invoice_only_access ? 'Wyłącz ograniczenie' : 'Włącz ograniczenie'}
                </Button>
              </div>
            )}

            <div className="pt-4 border-t">
              <div className="flex gap-2">
                <Badge variant={user.invoice_access ? 'success' : 'default'}>
                  {user.invoice_access ? 'Ma dostęp do faktur' : 'Brak dostępu do faktur'}
                </Badge>
                {user.invoice_access && (
                  <Badge variant={user.invoice_only_access ? 'warning' : 'info'}>
                    {user.invoice_only_access ? 'Tylko faktury' : 'Faktury + urlopy'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* NIP Management */}
      {user.invoice_access && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Przypisane NIPy</CardTitle>
            </CardHeader>
            <CardContent>
              {nipAccess && nipAccess.length > 0 ? (
                <div className="space-y-3">
                  {nipAccess.map((nip: NipAccess) => (
                    <div key={nip.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <span className="font-medium">{nip.nip}</span>
                        <p className="text-sm text-gray-500">
                          Przypisano przez: {nip.assignedBy.first_name} {nip.assignedBy.last_name}
                          {' '}({new Date(nip.created_at).toLocaleDateString()})
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeNip.mutate(nip.id)}
                        disabled={removeNip.isPending}
                      >
                        <TrashIcon className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  Brak przypisanych NIPów. Użytkownik ma dostęp do wszystkich faktur.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Przypisz nowe NIPy</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    NIPy (jeden na linię)
                  </label>
                  <textarea
                    {...register('nips', { 
                      required: 'Podaj co najmniej jeden NIP',
                      validate: (value) => {
                        const nips = value.split('\n').map(n => n.trim()).filter(n => n.length > 0);
                        return nips.length > 0 || 'Podaj co najmniej jeden NIP';
                      }
                    })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    rows={5}
                    placeholder="123-456-78-90&#10;987-654-32-10"
                  />
                  {errors.nips && (
                    <p className="mt-1 text-sm text-red-600">{errors.nips.message}</p>
                  )}
                </div>

                <div className="flex justify-end gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/users')}
                  >
                    Powrót
                  </Button>
                  <Button
                    type="submit"
                    isLoading={assignNips.isPending}
                  >
                    Przypisz NIPy
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};